import { routeM1TestShackManager } from "@/setup/routes/products.routes";
import {
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment } from "react";

import { TEST_SHACK_REPORT_STATUS } from "@/pages/products/test-shack/models/TestShackReport";
import { YES_NO_CODES } from "../../constants/inspectionChecks";
import { MeasurementValue } from "@/pages/products/test-shack/shared/components/MeasurementValue";
import { TestShackReportInspectionMetaRow } from "@/pages/products/test-shack/shared/components/report/TestShackReportInspectionMetaRow";
import { TestShackReportStatusArrow as StatusArrow } from "@/pages/products/test-shack/shared/components/report/TestShackReportStatusArrow";
import type {
	TestShackReportCell,
	TestShackReportColumn,
	TestShackReportSectionRev,
} from "../models/TestShackReportSectionRev";
import {
	formatReading,
	formatSpecMeasurement,
	isOutOfSpec,
	showsNoSpecMarkers,
} from "../utils/format";
import { TestShackReportNoSpecDot } from "@/pages/products/test-shack/shared/components/report/TestShackReportNoSpec";
import {
	numericCell,
	SPEC_ROWS,
	stickyLabelCell,
} from "@/pages/products/test-shack/shared/components/report/testShackReportTable";

interface TestShackReportDimensionTableProps {
	sectionRev: TestShackReportSectionRev;
	showMinTargetMax: boolean;
}

const ReadingCell = ({
	cell,
	column,
	isEvaluated,
}: {
	cell: TestShackReportCell | null;
	column: TestShackReportColumn;
	/** False when the Section-Rev has no specification to judge against. */
	isEvaluated: boolean;
}) => {
	const status = cell?.status ?? TEST_SHACK_REPORT_STATUS.NO_SPEC;
	// NG and No are judged without tolerances, so they stay red without a specification.
	const hasIssue =
		cell !== null &&
		(status === TEST_SHACK_REPORT_STATUS.NO_GO ||
			(isEvaluated && isOutOfSpec(status)));
	const isMissing = cell?.value == null && cell?.checked == null;
	const missingColor = YES_NO_CODES.has(column.code)
		? "text.disabled"
		: isEvaluated
			? "error.main"
			: "text.primary";

	return (
		<TableCell
			data-test-id={`report-reading-${column.code}`}
			align="center"
			sx={{
				...numericCell,
				backgroundColor: hasIssue
					? (theme) => alpha(theme.palette.error.main, 0.08)
					: undefined,
				color: hasIssue
					? "error.main"
					: isMissing
						? missingColor
						: "text.primary",
				fontWeight: hasIssue ? 700 : 400,
				minWidth: column.valueTypeCode === "A" ? 36 : 44,
			}}
		>
			<Stack
				direction="row"
				spacing={0.25}
				justifyContent="center"
				alignItems="center"
			>
				<MeasurementValue>{formatReading(cell, column)}</MeasurementValue>
				{hasIssue && <StatusArrow status={status} />}
			</Stack>
		</TableCell>
	);
};

export const M1TestShackReportDimensionTable = ({
	sectionRev,
	showMinTargetMax,
}: TestShackReportDimensionTableProps) => {
	const { columns, columnGroups, rows } = sectionRev;
	const showNoSpec = showsNoSpecMarkers(columns);

	if (columns.length === 0)
		return (
			<Typography color="text.secondary" sx={{ py: 2 }}>
				No persisted dimensions are available for this Section-Rev.
			</Typography>
		);

	return (
		<TableContainer
			component={Paper}
			variant="outlined"
			sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
		>
			<Table
				size="small"
				aria-label={`Inspections for ${sectionRev.sectionName}`}
				sx={{ "& .MuiTableCell-root": { px: 0.375, py: 0.5 } }}
			>
				<TableHead>
					<TableRow sx={{ backgroundColor: "action.hover" }}>
						<TableCell
							rowSpan={2}
							scope="col"
							sx={{
								...stickyLabelCell,
								backgroundColor: "transparent",
								color: "text.secondary",
								fontSize: "11px",
								fontWeight: 600,
							}}
						>
							Dims.
						</TableCell>
						{columnGroups.map((group, index) => (
							<TableCell
								key={`${group.key}-${index}`}
								data-test-id={`report-dimension-group-${group.key}`}
								align="center"
								colSpan={group.columnCount}
								scope="colgroup"
								sx={{
									color: "text.secondary",
									fontSize: "12px",
									fontWeight: 700,
									whiteSpace: "nowrap",
								}}
							>
								{group.name}
							</TableCell>
						))}
					</TableRow>
					<TableRow sx={{ backgroundColor: "action.hover" }}>
						{columns.map((column) => (
							<TableCell
								key={column.key}
								data-test-id={`report-dimension-${column.code}`}
								align="center"
								scope="col"
								sx={{
									color: "text.secondary",
									fontSize: "11px",
									fontWeight: 700,
									letterSpacing: "0.04em",
									minWidth: column.valueTypeCode === "A" ? 36 : 44,
								}}
							>
								<Tooltip title={column.name}>
									<span>
										{column.displayName}
										{showNoSpec && !column.hasSpec && (
											<TestShackReportNoSpecDot />
										)}
									</span>
								</Tooltip>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{showMinTargetMax &&
						SPEC_ROWS.map((specRow) => (
							<TableRow key={specRow.id}>
								<TableCell
									component="th"
									scope="row"
									sx={{
										...stickyLabelCell,
										color: "text.secondary",
										fontSize: "12px",
									}}
								>
									{specRow.label}
								</TableCell>
								{columns.map((column) => (
									<TableCell
										key={column.key}
										align="center"
										sx={{
											...numericCell,
											color: "text.secondary",
											fontWeight: specRow.isEmphasized ? 700 : 400,
										}}
									>
										<MeasurementValue>
											{formatSpecMeasurement(specRow.value(column), column)}
										</MeasurementValue>
									</TableCell>
								))}
							</TableRow>
						))}

					{rows.map((row) => (
						<Fragment key={row.inspection.ProfileInspectionId}>
							<TestShackReportInspectionMetaRow
								row={row}
								columnCount={columns.length + 1}
								managerRoute={routeM1TestShackManager}
							/>
							<TableRow
								data-test-id={`report-readings-${row.inspection.ProfileInspectionId}`}
							>
								<TableCell
									component="th"
									scope="row"
									sx={{
										...stickyLabelCell,
										color: "text.secondary",
										fontSize: "12px",
									}}
								>
									Entered
								</TableCell>
								{row.cells.map((cell, index) => (
									<ReadingCell
										key={columns[index]?.key ?? String(index)}
										cell={cell}
										column={columns[index]}
										isEvaluated={sectionRev.hasTolerances}
									/>
								))}
							</TableRow>
						</Fragment>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
